import {CallParticipant, CallService, CallState} from './call.service';
import {CallPhone, CallVoices} from './call-media.port';

/** One participant of a group call as a list shows them: who, where they stand, and their voice. */
export interface CallParticipantRow {
  email: string;
  name: string;
  initials: string;
  label: string;
  inCall: boolean;
  speaking: boolean;
  muted: boolean;
  isHost: boolean;
  /** Where the row sorts: in the call first, then ringing, then the rest. */
  rank: number;
  /**
   * Set on a phone person (ITP-E-198-01): what hangUpPhone takes, their number masked, and whether they are still being
   * dialled — the row's button is Cancel then, Hang up once they answered. A phone person never gets Join, consent or chat.
   */
  phone?: {key: string; masked: string; dialling: boolean};
}

/** Where each status sorts: who is in the call first, then who is still being rung, then the rest. */
const ORDER: Record<string, number> = {Joined: 0, Ringing: 1, Busy: 2, Declined: 3, Missed: 4, Left: 5};

/**
 * Everyone in a group call with where they stand (ITP-E-172-01, moved out of the group call panel by ITP-E-182-04):
 * in the call (speaking, muted or reconnecting), being rung, declined, no answer, busy or left. Members of a large group
 * who were not rung are left out until they join. ITP-E-180-09: a member being rung reads "Calling…" until a tab of
 * theirs shows the ring, then "Ringing…", or "Offline" when no ITP tab of theirs is open; a member on Do not disturb
 * reads "Unavailable". A ring that ran out is still No answer.
 *
 * ITP-E-198-01: a phone person added to the call is a row too — "📞 <name>" with the number masked, Calling… and
 * Ringing… while dialled, then in the call with their mute and speaking. A 1:1 call with a phone person lists the two
 * people as well, so its Participants says who is in it.
 */
export class CallParticipants {
  /**
   * The rows of a group call this tab is in or joining, its phone people with them (ITP-E-198-01); a 1:1 call has rows only
   * once a phone person is in it or being dialled — the two people, then the phones. None outside a call. `self` is this
   * side's identity in the media, for a 1:1 call's own row.
   */
  static of(state: CallState, voices: CallVoices, now: number, phones: CallPhone[] = [], self: string | null = null): CallParticipantRow[] {
    if (state.phase !== 'joining' && state.phase !== 'connected') {
      return [];
    }
    if (!state.isGroup) {
      return phones.length ? CallParticipants.sorted([...CallParticipants.rows(CallParticipants.pair(state, self), voices ?? {}),
        ...CallParticipants.phoneRows(phones)]) : [];
    }
    // The server reads a ring older than 30 seconds as Missed but says so only on the next change; so does the list.
    const rungOut = state.phase === 'connected' && now - (state.joinedAt ?? state.connectedAt ?? now) >= CallService.ringTimeoutMs;
    const rows = CallParticipants.rows(state.participants ?? [], voices ?? {}, rungOut);
    return phones.length ? CallParticipants.sorted([...rows, ...CallParticipants.phoneRows(phones)]) : rows;
  }

  /** A phone person's row (ITP-E-198-01): Calling… / Ringing… while dialled, then in the call with their voice. */
  static phoneRows(phones: CallPhone[]): CallParticipantRow[] {
    return phones.map(phone => {
      const inCall = phone.status === 'in-call';
      const muted = inCall && phone.muted;
      const speaking = inCall && !muted && phone.speaking;
      return {
        email: phone.key,
        name: phone.name,
        initials: CallParticipants.initials(phone.name),
        label: !inCall ? (phone.status === 'ringing' ? 'Ringing…' : 'Calling…') : speaking ? 'Speaking' : muted ? 'Muted' : 'In the call',
        inCall,
        speaking,
        muted,
        isHost: false,
        rank: inCall ? ORDER['Joined'] : ORDER['Ringing'],
        phone: {key: phone.key, masked: phone.masked, dialling: !inCall}
      };
    });
  }

  /** A 1:1 call's two people, as participants (ITP-E-198-01): the other person, and this side as You; the caller hosts. */
  private static pair(state: CallState, self: string | null): CallParticipant[] {
    const status = state.phase === 'connected' ? 'Joined' as const : 'Ringing' as const;
    return [
      {email: state.peerEmail || 'peer', name: state.peerName || state.peerEmail || '', status, isHost: !state.isCaller,
        away: state.reconnecting === 'peer'},
      {email: self || 'self', name: 'You', status: 'Joined', isHost: !!state.isCaller}
    ];
  }

  private static sorted(rows: CallParticipantRow[]): CallParticipantRow[] {
    return rows.sort((a, b) => a.rank - b.rank || Number(b.isHost) - Number(a.isHost));
  }

  static rows(participants: CallParticipant[], voices: CallVoices, rungOut = false): CallParticipantRow[] {
    return participants
      .filter(participant => participant.status !== 'NotRung')
      .map(participant => rungOut && participant.status === 'Ringing' ? {...participant, status: 'Missed' as const} : participant)
      .map(participant => {
        const voice = voices[(participant.email || '').toLowerCase()];
        const inCall = participant.status === 'Joined';
        const muted = inCall && !!voice?.muted;
        const speaking = inCall && !muted && !!voice?.speaking && !participant.away;
        return {
          email: participant.email,
          name: participant.name || participant.email,
          initials: CallParticipants.initials(participant.name || participant.email),
          label: CallParticipants.label(participant, muted, speaking),
          inCall,
          speaking,
          muted,
          isHost: !!participant.isHost,
          rank: ORDER[participant.status] ?? 9
        };
      })
      .sort((a, b) => a.rank - b.rank || Number(b.isHost) - Number(a.isHost));
  }

  static label(participant: CallParticipant, muted: boolean, speaking: boolean): string {
    switch (participant.status) {
      case 'Joined':
        return participant.away ? 'Reconnecting…' : speaking ? 'Speaking' : muted ? 'Muted' : 'In the call';
      case 'Ringing':
        // ITP-E-180-09: "Ringing…" only once a tab of theirs shows the ring.
        return participant.ringDelivered ? 'Ringing…' : participant.offline ? 'Offline' : 'Calling…';
      case 'Declined':
        return 'Declined';
      case 'Missed':
        return participant.unavailable ? 'Unavailable' : 'No answer';
      case 'Busy':
        return 'Busy';
      case 'Left':
        return 'Left';
      default:
        return '';
    }
  }

  static initials(name: string): string {
    const words = (name || '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    return words.slice(0, 2).map(word => word[0].toUpperCase()).join('') || '?';
  }

  /**
   * Carbon's avatar pair for a name, by its first letter: A-H, I-N, O-T, U-Z, or the default for anything else — the
   * `data-tone` of an initials disc (`carbon-avatar-tones` in `_call-tokens.scss`).
   */
  static avatarTone(name: string | undefined): string {
    const letter = (name || '').trim().charAt(0).toUpperCase();
    if (letter >= 'A' && letter <= 'H') {
      return 'ah';
    }
    if (letter >= 'I' && letter <= 'N') {
      return 'in';
    }
    if (letter >= 'O' && letter <= 'T') {
      return 'ot';
    }
    return letter >= 'U' && letter <= 'Z' ? 'uz' : 'default';
  }
}
